import React, { useCallback, useState } from "react";
import { FormattedMessage, useIntl } from 'react-intl'
import classnames from 'classnames';
import {  useMediaQuery, useTheme } from "@material-ui/core";

import { makeBtStyles } from '../Theme';
import { useFeatureEnables } from "../dataProviders";

import { BtLink } from "../components/BtLink";
import { isGiftWrapped } from "../components/GiftWrapPanel";
import { LineItemCount } from "../components/LineItems";
import TrackingEvents from '../components/TrackingEvents';

import { CardSection } from './Card';
import LineItemsCard, { useIsLineItemsCardShown } from './LineItemsCard';


const useStyles = makeBtStyles( theme => ({
  lineItemsCard: {
    border: 'none',
    margin: '0 0 0 20px',
    boxShadow: 'none',
    [theme.breakpoints.up('md')]: {
      width: '280px',
      margin: '0 0 0 25px',
      maxHeight: '100%'
    },
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  shipmentActivity: ({ showGiftLink }:{showGiftLink: boolean}) => ({
    padding: '20px 20px 10px 32px',
    display: 'flex',
    flexDirection: 'row',
    [theme.breakpoints.up('lg')]: {
      maxHeight: ( showGiftLink ? '325px' : '355px'),
    },
    [theme.breakpoints.only('md')]: {
      padding: '0px 20px 10px 32px',
      height: '300px'
    },
    [theme.breakpoints.down('sm')]: {
      border: '1px solid #DFE3E8',
      borderTop: 'none',
      padding: '20px 20px 10px 20px',
      background: '#FFFFFF',
      margin: 'auto',
      marginTop: '0',
      width: '95% !important',
      maxWidth: '625px'
    }
  }),
  scrollableSection: ({ showGiftLink }:{showGiftLink: boolean}) => ({
    overflow: 'auto',
    width: '280px',
    paddingRight: '20px',
    marginRight: '45px',
    [theme.breakpoints.up('lg')]: {
      width: '300px',
      height: ( showGiftLink ? '240px' : '310px'),
    },
    [theme.breakpoints.only('md')]: {
      height: '300px',
    },
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  }),
  eventRow: () => {
    return {
      [theme.breakpoints.down('sm')]: {
        padding: '0 0 20px 42px',
      },
      padding: '0 0 10px 42px',
      position: 'relative',
      color: '#929292',
      '&::before': {
        content: '""',
        width: '4px',
        backgroundColor: '#dedede',
        position: 'absolute',
        left: '20px',
        [theme.breakpoints.up('md')]: {
          height: '100%',
          top: '-10px',
        },
        [theme.breakpoints.down('sm')]: {
          height: '100%',
          top: '-20px'
        },
      },
      '&.mostRecentEvent': {
        color: 'inherit',
        '&::before': {
          backgroundColor: `${theme.colors.characteristic ? theme.colors.characteristic : theme.colors.main}`,
          top: '0',
          [theme.breakpoints.up('md')]: {
            height: '90%',
          },
          [theme.breakpoints.down('sm')]: {
            height: '90%',
          }
        }
      }
    };
  },
  shipmentTabs: {
    display: 'flex',
    background: '#FFFFFF',
    minHeight: '40px',
    [theme.breakpoints.down('sm')]: {
      margin: 'auto',
      marginBottom: '0',
      width: '95% !important',
      maxWidth: '625px',
    },
    [theme.breakpoints.only('md')]: {
      margin: '25px 50px',
      minHeight: '0px',
    }
  },
  tab: ({ lineItemsCardShown }:{lineItemsCardShown: boolean}) => ({
    fontSize: '14px',
    lineSpacing: '17px',
    letterSpacing: '0.13px',
    fontWeight: 'bold',
    color: '#000000',
    width: '50%',
    textDecoration: 'none !important',
    [theme.breakpoints.up('lg')]: {
      textAlign: 'left',
      margin: '20px 0 0 52px',
    },
    [theme.breakpoints.up('md')]: {
      borderTop: 'none',
    },
    [theme.breakpoints.down('sm')]: {
      border: '1px solid #DEDEDE',
      padding: (lineItemsCardShown ? '13px 0px' : '20px 0'),
      width: (lineItemsCardShown ? '50%' : '100%'),
      textAlign: 'center',
      borderBottom: (lineItemsCardShown ? '' : 'none'),
    }
  }),
  activeTab: {
    borderBottom: 'none !important',
  },
  inactiveTab: {
    background: '#F6F5F5'
  },
  leftTab: {
    [theme.breakpoints.down('sm')]: {
      borderRight: 'none !important',
    }
  },
  rightTab: {
    [theme.breakpoints.only('md')]: {
      marginLeft: '80px'
    }
  }
}));

const useTrackingStyles = makeBtStyles( theme => ({
  event: {
    '& .timestamp': {
      fontSize: '12px',
      fontWeight: '700',
      lineHeight: '12px',
      display: 'block',
      textTransform: 'uppercase',
      marginBottom: '2px'
    },
    '& .description, & .location': {
      fontSize: '14px',
      fontWeight: '300',
      lineHeight: '14px',
      letterSpacing: '0'
    },
    '& .location': {
      marginLeft: '3px'
    }
  },
  emptyMsg: {
    padding: '15px 20px',
    fontStyle: 'italic',
    color: '#929292'
  },
  collapseToggle: {
    [theme.breakpoints.up('md')]:{
      position: 'relative',
      bottom: '-24px',
      marginLeft: '20px',
    },
    [theme.breakpoints.down('sm')]:{
      padding: '10px 20px',
    },
    fontSize: '14px',
    textTransform: 'lowercase',
    width: '100%',
  },
  showMore: {
    display: 'inline-block',
  }
}));

export default function ShipmentActivity({className} : { className?: string }) {

  const intl = useIntl();

  const lineItemsCardShown = useIsLineItemsCardShown();
  const { showGiftLink } = useFeatureEnables();

  const classes = useStyles({ showGiftLink, lineItemsCardShown });
  const trackingClasses = useTrackingStyles();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const formatTimestamp = (timestamp: Date) => {
    const date = intl.formatDateToParts(timestamp, { day: 'numeric', month: 'long' }).map( p => p.value ).join('');
    const time = intl.formatDateToParts(timestamp, { hour: 'numeric', minute: 'numeric' }).map( p => p.value ).join('');
    return `${date} - ${time}`;
  };

  const eventClassName = (index: number) => (
  classnames(classes.eventRow, { 'mostRecentEvent': index === 0 })
  );

  const [showActivity, setShowActivity] = useState(true);
  const toggleShowActivity = useCallback( () => setShowActivity(true), [] );
  const toggleShowContent = useCallback( () => setShowActivity( false ), [] );


  return ( <>
    <div className={classes.shipmentTabs}>
      { isMobile ? ( <>
        <BtLink className={`${classes.tab} ${classes.leftTab} ${showActivity ? classes.activeTab : classes.inactiveTab} `} onClick={toggleShowActivity}>
          { (isGiftWrapped) ? (
            <FormattedMessage description="layout03.shipmentTabs.activityAsGift" defaultMessage="Gift Activity" />
          ):(
            <FormattedMessage description="layout03.shipmentTabs.activity" defaultMessage="Shipment Activity" />
          )}
        </BtLink>
        { lineItemsCardShown &&
          <BtLink className={`${classes.tab} ${classes.rightTab} ${ !showActivity ? classes.activeTab : classes.inactiveTab}`}  onClick={toggleShowContent}>
            <FormattedMessage description="layout03.shipmentTabs.content" defaultMessage="Shipment Contents" />
            {" ("}
            <LineItemCount />
            {")"}
          </BtLink>
        }
      </>) : <> 
        <div className={classes.tab}>
          { (isGiftWrapped) ? (
            <FormattedMessage description="layout03.shipmentTabs.activityAsGift" defaultMessage="Gift Activity" />
          ):(
            <FormattedMessage description="layout03.shipmentTabs.activity" defaultMessage="Shipment Activity" />
          )}
        </div>
        { lineItemsCardShown &&
          <div className={`${classes.tab} ${classes.rightTab}`}>
            <FormattedMessage description="layout03.shipmentTabs.content" defaultMessage="Shipment Contents" />
            {" ("}
            <LineItemCount />
            {")"}
          </div>
        }
      </>
      }
    </div>
    <div className={classes.shipmentActivity}>
      { (showActivity || !isMobile) && 
        <div className={classes.scrollableSection} role="list">
          <TrackingEvents
            classes={trackingClasses}
            truncateToCount={showGiftLink ? 3 : 4}
            formatTimestamp={formatTimestamp}
            eachEvent={ ({ content,key,index,event }) => (
              <CardSection className={eventClassName(index)} key={key}>
                {content}
              </CardSection>
            )}
          />
        </div>
      }
      { (!showActivity || !isMobile) && 
        <LineItemsCard className={classes.lineItemsCard} />
      }
    </div>
  </>)
}